// The frame of every People and organisation page (/org...). The section's
// areas (Organisation · {collabLabelPlural} · Insight · My tasks, and the
// Settings gear) are in the app header's main navigation (shell/Header.tsx
// uses orgNavItems and orgSection below), so there is no second tab bar here:
// OrgShell gives the pages one container and keeps the organisation's words
// fresh for the header. OrgLayout is the old name; existing pages keep it.
import "../workforce.css";
import { useEffect, useSyncExternalStore, type ReactNode } from "react";
import { useOrgWords } from "../queries";

export type OrgNavKey = "organisation" | "collabs" | "insight" | "tasks" | "settings";

export type OrgNavItem = { key: OrgNavKey; to: string; label: string };

/** The header's main-nav items on /org pages; the second uses the organisation's own word. Settings is the gear at the end. */
export function orgNavItems(collabLabelPlural: string): OrgNavItem[] {
  return [
    { key: "organisation", to: "/org", label: "Organisation" },
    { key: "collabs", to: "/org/collabs", label: collabLabelPlural },
    { key: "insight", to: "/org/insight", label: "Insight" },
    { key: "tasks", to: "/org/tasks", label: "My tasks" },
    { key: "settings", to: "/org/settings", label: "Settings" },
  ];
}

const INSIGHT = /^\/org\/(insight|goals|analytics|workload|ogsm|kpis)(\/|$)/;
const TASKS = /^\/org\/(tasks|my-quarter|confirm|to-report)(\/|$)/;

/** Which area of the section a path is in (null outside /org). */
export function orgSection(pathname: string): OrgNavKey | null {
  if (!/^\/org(\/|$)/.test(pathname)) return null;
  if (/^\/org\/collabs(\/|$)/.test(pathname)) return "collabs";
  if (INSIGHT.test(pathname)) return "insight";
  if (TASKS.test(pathname)) return "tasks";
  if (/^\/org\/settings(\/|$)/.test(pathname)) return "settings";
  return "organisation";
}

// A page whose place depends on its data (a KPI of a unit's plan sits under
// Organisation, of a collab's plan under {collabs}, of the organisation plan
// under Insight) tells the header which area to highlight, the same as its
// breadcrumb. It holds for that address only and ends when the page goes.
let sectionOverride: { pathname: string; key: OrgNavKey } | null = null;
const overrideListeners = new Set<() => void>();
const setSectionOverride = (next: typeof sectionOverride) => {
  sectionOverride = next;
  overrideListeners.forEach((l) => l());
};
const subscribeOverride = (l: () => void) => {
  overrideListeners.add(l);
  return () => overrideListeners.delete(l);
};

/** On a page: highlight this area in the header (null: follow the address). */
export function useOrgSectionFor(pathname: string, key: OrgNavKey | null) {
  useEffect(() => {
    if (!key) return;
    setSectionOverride({ pathname, key });
    return () => {
      if (sectionOverride?.pathname === pathname && sectionOverride.key === key) setSectionOverride(null);
    };
  }, [pathname, key]);
}

/** The header's area for a path: what the page said (useOrgSectionFor), else orgSection. */
export function useOrgSection(pathname: string): OrgNavKey | null {
  const o = useSyncExternalStore(subscribeOverride, () => sectionOverride);
  return o && o.pathname === pathname ? o.key : orgSection(pathname);
}

/** The page frame: one container, no second navigation. */
export function OrgShell({ children }: { children: ReactNode }) {
  // Loads (and caches) the words the header shows, also on a page that does not ask for the settings itself.
  useOrgWords();
  return <div className="org-shell">{children}</div>;
}

/** The old name of OrgShell. */
export const OrgLayout = OrgShell;
